import { css, cx } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Button, EmptyStateOnboarding, EmptyStatePlatformHubIllustration, ExternalLink, SimpleDataTable, Pagination } from "@octopusdeploy/design-system-components";
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetProcessTemplateSummariesResponse, PlatformHubVersionControlSettingsResource, ProcessTemplateSummary, Repository, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { IsDefaultBranch, isProtectedBranch, Permission, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useAnalyticProcessTemplateCreatedDispatch } from "~/analytics/Analytics";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import { TemplateNameCell } from "~/areas/platformHub/TemplateNameCell";
import { TemplateOverflowMenu } from "~/areas/platformHub/TemplateOverflowMenu";
import type { CreateProcessTemplateDialogProps } from "~/areas/platformHub/processTemplates/CreateProcessTemplateDialog";
import { CreateProcessTemplateDialog } from "~/areas/platformHub/processTemplates/CreateProcessTemplateDialog";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import Markdown from "~/components/Markdown/index";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { withTheme } from "~/components/Theme/index";
import WarningIcon from "~/components/WarningIcon/index";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import DateFormatter from "~/utils/DateFormatter/index";
interface LoaderData {
    processTemplatesResponse: GetProcessTemplateSummariesResponse;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
}
interface ProcessTemplatesPageProps {
    loaderData: LoaderData;
    queryParams: ProcessTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProcessTemplatesPageQueryParams>;
}
export interface ProcessTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyProcessTemplatesResponse: GetProcessTemplateSummariesResponse = {
    ProcessTemplateSummaries: [],
    TotalResults: 0,
    ItemsPerPage: 0,
    TotalNoOfProcessTemplates: 0,
};
export async function processTemplatesPageLoader(repository: Repository, queryParams: ProcessTemplatesPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            processTemplatesResponse: emptyProcessTemplatesResponse,
            persistenceSettings,
            gitRefValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            processTemplatesResponse: emptyProcessTemplatesResponse,
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const userPageSize = getValueInStorage(`Octopus.ProcessTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const processTemplatesResponse = await repository.ProcessTemplates.getSummaries({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: gitRefValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        processTemplatesResponse,
        persistenceSettings,
        gitRefValidationResult,
    };
}
export function ProcessTemplatesPage({ loaderData, queryParams, setQueryParams }: ProcessTemplatesPageProps) {
    const [gitRef, setGitRef, validationResult] = usePlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.gitRefValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProcessTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyProcessTemplatesResponse);
        }
        if (validationResult && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
            return Promise.resolve(emptyProcessTemplatesResponse);
        }
        return repository.ProcessTemplates.getSummaries({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize, validationResult], "Get paginated process templates", {
        initialResult: loaderData.processTemplatesResponse,
    });
    const dispatchProcessTemplateCreated = useAnalyticProcessTemplateCreatedDispatch(IsDefaultBranch(loaderData.persistenceSettings, gitRef) ?? false, isProtectedBranch(loaderData.gitRefValidationResult?.GitRef));
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const { execute: deleteProcessTemplate } = useMutation({
        name: "Delete Process Template",
        action: (repository: Repository, processTemplate: ProcessTemplateSummary) => repository.ProcessTemplates.del({ ProcessTemplateSlug: processTemplate.Slug, GitRef: processTemplate.GitRef, ChangeDescription: `Delete Process Template '${processTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    if (!gitRef || !loaderData.persistenceSettings.Url) {
        return (<PageContent header={{ title: "Process Templates" }}>
                <ConfigureVersionControlOnboarding />
            </PageContent>);
    }
    const createProcessTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Create Process Template",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <CreateProcessTemplateButton gitRef={gitRef} dispatchProcessTemplateCreated={dispatchProcessTemplateCreated}/>,
    };
    const showOnboarding = pagingData.TotalNoOfProcessTemplates === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    if (validationResult !== undefined && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return (<PageContent header={{ title: "Process Templates", contextSelector: titleAccessory }}>
                <PlatformHubVcsError error={validationResult} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    if (showOnboarding) {
        return (<PageContent header={{ title: "Process Templates", contextSelector: titleAccessory }}>
                <AddProcessTemplatesOnboarding primaryAction={createProcessTemplateAction}/>
            </PageContent>);
    }
    return (<PageContent header={{ title: "Process Templates", primaryAction: createProcessTemplateAction, contextSelector: titleAccessory }} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Process Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <ProcessTemplatesTable processTemplates={pagingData.ProcessTemplateSummaries} deleteProcessTemplate={deleteProcessTemplate}/>
        </PageContent>);
}
interface ProcessTemplatesTableProps {
    processTemplates: ProcessTemplateSummary[];
    deleteProcessTemplate: (processTemplate: ProcessTemplateSummary) => Promise<void>;
}
function ProcessTemplatesTable({ processTemplates, deleteProcessTemplate }: ProcessTemplatesTableProps) {
    return (<SimpleDataTable data={processTemplates} columns={[
            {
                title: "Name",
                render: (pt) => <TemplateNameCell name={pt.Name} icon={pt.Icon} linkTarget={links.editProcessTemplatePage.generateUrl({ slug: pt.Slug }, { gitRef: pt.GitRef })}/>,
                columnSize: "medium",
            },
            {
                title: "Description",
                render: (pt) => (<div className={cx(styles.rowCellPadding, styles.descriptionContainer)}>
                            {pt.HasError && withTheme((theme) => <WarningIcon color={theme.errorText}/>)}
                            <Markdown markup={pt.Description ?? ""}/>
                        </div>),
                columnSize: "medium",
            },
            {
                title: "Last Published Version",
                render: (pt) => (<div className={cx(styles.rowCellPadding, styles.publishedDateContainer)} style={pt.Version && pt.PublishedDate ? { justifyContent: "space-between" } : { justifyContent: "end" }}>
                            {pt.Version && pt.PublishedDate ? (<span>
                                    {pt.Version} ({DateFormatter.dateToLongFormat(pt.PublishedDate)})
                                </span>) : null}
                            <TemplateOverflowMenu templateName={pt.Name} templateTypeLabel="process template" onDelete={() => deleteProcessTemplate(pt)}/>
                        </div>),
                columnSize: "medium",
            },
        ]} getRowKey={(pt) => pt.Slug}/>);
}
const AddProcessTemplatesOnboarding = ({ primaryAction }: {
    primaryAction?: PrimaryPageAction;
}) => (<EmptyStateOnboarding title="Standardize your deployment processes" description="Process templates are resuable, standardized blocks of steps that can be used in deployment processes or runbooks with flexibility for app teams." learnMore={<ExternalLink href="process-templates">Docs: Process Templates</ExternalLink>} primaryAction={primaryAction} image={<EmptyStatePlatformHubIllustration />}/>);
const ConfigureVersionControlOnboarding = () => (<OnboardingPage title="Configure your Git connection" intro={<>
                <p>Process templates are a group of reusable steps for projects, stored in a Git repository.</p>
                <p>To add a process template, you’ll need to set up a Git connection in Platform Hub.</p>
            </>} learnMore={null} actionButtons={<Button label={"Configure Git Connection"} href={links.platformHubVersionControlSettingsPage.generateUrl()} importance="primary"/>}/>);
function CreateProcessTemplateButton({ gitRef, dispatchProcessTemplateCreated }: CreateProcessTemplateDialogProps) {
    return (<OpenDialogButton disabled={false} label={"Create Process Template"} title={"Create new process template"} type={ActionButtonType.Primary}>
            <CreateProcessTemplateDialog gitRef={gitRef} dispatchProcessTemplateCreated={dispatchProcessTemplateCreated}/>
        </OpenDialogButton>);
}
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    descriptionContainer: css({
        wordBreak: "break-word",
    }),
    publishedDateContainer: css({
        display: "flex",
        alignItems: "center",
    }),
};
